import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import './TutorWallet.css';

const API = 'https://mykaddle-api.onrender.com/api/banking';
const WITHDRAWAL_FEE = 5;
const MIN_WITHDRAWAL = 300;

// ── Transaction item ─────────────────────────────────────────
const txLabel = {
  escrow_hold:         'Payment hold', 
  escrow_release:      'Escrow released',
  wallet_credit:       'Payment credited',
  withdrawal_request:  'Withdrawal',
  withdrawal_paid:     'Withdrawal paid',
  refund:              'Refund',
  payment_received:    'Payment received',
  platform_commission: 'Platform commission',
};

const txType = (entry_type) => {
  if (['withdrawal_request','withdrawal_paid','platform_commission','refund'].includes(entry_type)) return 'withdrawal';
  if (entry_type === 'escrow_hold') return 'pending'; 
  return 'received';
};

const TxItem = ({ tx }) => {
  const type = txType(tx.entry_type);
  const date = new Date(tx.created_at).toLocaleDateString('en-ZA', { day:'numeric', month:'short', year:'numeric' });
  return (
    <div className="tw-tx-item">
      <div className={`tw-tx-icon-wrap ${type}`}>
        {type === 'received'
          ? <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 19V5M5 12l7-7 7 7"/></svg>
          : type === 'pending'
          ? <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/></svg>
          : <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 5v14M5 12l7 7 7-7"/></svg>
        }
      </div>
      <div className="tw-tx-detail">
        <div className="tw-tx-desc">{txLabel[tx.entry_type] || tx.entry_type}</div>
        {tx.notes && <div className="tw-tx-sub">{tx.notes}</div>}
      </div>
      <div className="tw-tx-right">
        <div className={`tw-tx-amount ${type}`}>
          {type === 'pending' ? '' : type === 'received' ? '+' : '−'}R {parseFloat(tx.amount).toFixed(2)}
        </div>
        <div className="tw-tx-date">{date}</div>
      </div>
    </div>
  );
};

// ── Bank modal ───────────────────────────────────────────────
const BankModal = ({ onClose, onSaved }) => {
  const [form, setForm] = useState({ bankName:'', accountHolder:'', accountNumber:'', branchCode:'', accountType:'Cheque' });
  const [confirmed, setConfirmed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [checked, setChecked] = useState(false);
  const [errors, setErrors]   = useState({});

  const SA_BANKS = ['ABSA','Capitec','FNB','Nedbank','Standard Bank','African Bank','Bidvest','Discovery Bank','Investec','Old Mutual','TymeBank','Ubank','Postbank'];

  const validate = () => {
    const e = {};
    if (!form.bankName)      e.bankName = true;
    if (!form.accountHolder) e.accountHolder = true;
    if (!form.accountNumber) e.accountNumber = true;
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/info`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(form)
      });
      if (!res.ok) throw new Error();
      setConfirmed(true);
      onSaved(form);
    } catch {
      alert('Failed to save banking details. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="tw-modal-backdrop" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="tw-modal">
        <button className="tw-modal-close" onClick={onClose}>✕</button>

        {!confirmed ? (
          <>
            <div className="tw-modal-icon-ring">
              <svg xmlns="http://www.w3.org/2000/svg" width="1em" height="1em" viewBox="0 0 24 24">
  <path d="M0 0h24v24H0z" fill="none" />
  <path fill="currentColor" fill-opacity=".25" d="M3 10c0-1.886 0-2.828.586-3.414S5.114 6 7 6h10c1.886 0 2.828 0 3.414.586S21 8.114 21 10v4c0 1.886 0 2.828-.586 3.414S18.886 18 17 18H7c-1.886 0-2.828 0-3.414-.586S3 15.886 3 14z" />
  <circle cx="6" cy="15" r="1" fill="currentColor" />
  <path fill="currentColor" d="M3 9h18v2H3z" />
</svg>

            </div>
            <div className="tw-modal-title">Payout bank account</div>
            <div className="tw-modal-sub">Enter the account where your earnings will be paid out.</div>

            <div className="tw-field">
              <label className="tw-field-label">Bank</label>
              <select
                value={form.bankName}
                onChange={e => setForm(f => ({ ...f, bankName: e.target.value }))}
                className={`tw-field-input ${errors.bankName ? 'tw-error' : ''}`}
              >
                <option value="">Select bank</option>
                {SA_BANKS.map(b => <option key={b} value={b}>{b}</option>)}
              </select>
            </div>

            <div className="tw-field">
              <label className="tw-field-label">Account holder name</label>
              <input
                type="text"
                placeholder="Full name as on bank account"
                value={form.accountHolder}
                onChange={e => setForm(f => ({ ...f, accountHolder: e.target.value }))}
                className={`tw-field-input ${errors.accountHolder ? 'tw-error' : ''}`}
              />
            </div>

            <div className="tw-row-fields">
              <div className="tw-field">
                <label className="tw-field-label">Account number</label>
                <input
                  type="text"
                  placeholder="e.g. 1234567890"
                  value={form.accountNumber}
                  onChange={e => setForm(f => ({ ...f, accountNumber: e.target.value.replace(/\D/g,'') }))}
                  className={`tw-field-input ${errors.accountNumber ? 'tw-error' : ''}`}
                />
              </div>
              <div className="tw-field">
                <label className="tw-field-label">Branch code</label>
                <input
                  type="text"
                  placeholder="e.g. 632005"
                  value={form.branchCode}
                  onChange={e => setForm(f => ({ ...f, branchCode: e.target.value.replace(/\D/g,'') }))}
                  className="tw-field-input"
                />
              </div>
            </div>

            <div className="tw-field">
              <label className="tw-field-label">Account type</label>
              <select value={form.accountType} onChange={e => setForm(f => ({ ...f, accountType: e.target.value }))} className="tw-field-input">
                <option>Cheque</option>
                <option>Savings</option>
                <option>Transmission</option>
                <option>Bond</option>
              </select>
            </div>

            <label className="tw-check-label">
              <input type="checkbox" checked={checked} onChange={e => setChecked(e.target.checked)} />
              <span>I confirm these details are correct and belong to me</span>
            </label>

            <button className="tw-btn-primary" onClick={handleSubmit} disabled={!checked || loading}>
              {loading ? <span className="tw-spinner" /> : 'Save bank details'}
            </button>
          </>
        ) : (
          <div className="tw-confirmed-wrap">
            <div className="tw-confirmed-icon">✓</div>
            <div className="tw-modal-title">Details saved</div>
            <div className="tw-modal-sub">Your payout account has been updated.</div>
            <div className="tw-warning-box">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
              <div><b>Withdrawals are frozen for 24 hours</b> as a security measure. You can still receive payments normally.</div>
            </div>
            <div className="tw-summary-box">
              <div className="tw-summary-label">New payout account</div>
              <div className="tw-summary-bank">{form.bankName} — {form.accountType}</div>
              <div className="tw-summary-acct">{form.accountHolder} · •••• {form.accountNumber.slice(-4)}</div>
            </div>
            <button className="tw-btn-primary" onClick={onClose}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{width:16,height:16}}><polyline points="20 6 9 17 4 12"/></svg>
              Got it, close
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// ── Withdrawal confirm modal ──────────────────────────────────
const WithdrawModal = ({ amount, netAmount, bankName, maskedAcct, onClose }) => (
  <div className="tw-modal-backdrop" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
    <div className="tw-modal tw-modal-sm">
      <div className="tw-confirmed-icon">✓</div>
      <div className="tw-modal-title">Withdrawal submitted</div>
      <div className="tw-modal-sub">Your withdrawal is being processed.</div>
      <div className="tw-fee-row"><span>Requested</span><span>R {parseFloat(amount).toFixed(2)}</span></div>
      <div className="tw-fee-row tw-fee-item"><span>Withdrawal fee</span><span>− R {WITHDRAWAL_FEE.toFixed(2)}</span></div>
      <div className="tw-divider"/>
      <div className="tw-fee-row tw-fee-net"><span>You receive</span><span>R {parseFloat(netAmount).toFixed(2)}</span></div>
      <div className="tw-bank-ref">{bankName} · {maskedAcct}</div>
      <button className="tw-btn-secondary" onClick={onClose}>Close</button>
    </div>
  </div>
);

// ── Main Page ────────────────────────────────────────────────
const TutorWallet = () => {
  const navigate = useNavigate();

  const [wallet,       setWallet]       = useState({ pending_amount:0, available_amount:0, total_earned:0 });
  const [transactions, setTransactions] = useState([]);
  const [banking,      setBanking]      = useState(null);
  const [filter,       setFilter]       = useState('all');
  const [loading,      setLoading]      = useState(true);

  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [showFeeBox,     setShowFeeBox]     = useState(false);
  const [withdrawing,    setWithdrawing]    = useState(false);

  const [showBankModal,     setShowBankModal]     = useState(false);
  const [showWithdrawModal, setShowWithdrawModal] = useState(false);
  const [withdrawResult,    setWithdrawResult]    = useState(null);

  const amountRef = useRef(null);
  const token = localStorage.getItem('token');
  const authHeaders = { Authorization: `Bearer ${token}` };

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [wRes, bRes] = await Promise.all([
        fetch(`${API}/wallet`,  { headers: authHeaders }),
        fetch(`${API}/info`,    { headers: authHeaders }),
      ]);
      const wData = await wRes.json();
      const bData = await bRes.json();
      setWallet(wData.wallet || { pending_amount:0, available_amount:0, total_earned:0 });
      setTransactions(wData.transactions || []);
      setBanking(bData);
    } catch (err) {
      console.error('Failed to fetch wallet data', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleAmountChange = (val) => {
    setWithdrawAmount(val);
    const n = parseFloat(val);
    setShowFeeBox(!isNaN(n) && n >= MIN_WITHDRAWAL && n <= wallet.available_amount);
  };

  const setQuick = (val) => {
    const v = val === 'full' ? wallet.available_amount : Math.min(val, wallet.available_amount);
    setWithdrawAmount(v.toFixed(2));
    setShowFeeBox(v >= MIN_WITHDRAWAL);
  };

  const netAmount = Math.max((parseFloat(withdrawAmount) || 0) - WITHDRAWAL_FEE, 0);

  const handleWithdraw = async () => {
    const n = parseFloat(withdrawAmount);
    if (isNaN(n) || n < MIN_WITHDRAWAL) {
      amountRef.current?.classList.add('tw-input-error');
      setTimeout(() => amountRef.current?.classList.remove('tw-input-error'), 1800);
      return;
    }
    setWithdrawing(true);
    try {
      const res = await fetch(`${API}/withdraw`, {
        method: 'POST',
        headers: { ...authHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify({ amount: n })
      });
      const data = await res.json();
      if (!res.ok) { alert(data.message); return; }
      setWithdrawResult(data.breakdown);
      setShowWithdrawModal(true);
      setWithdrawAmount('');
      setShowFeeBox(false);
      fetchAll();
    } catch {
      alert('Withdrawal failed. Please try again.');
    } finally {
      setWithdrawing(false);
    }
  };

  const filtered = transactions.filter(tx => {
    if (filter === 'all') return true;
    return txType(tx.entry_type) === filter;
  });

  const available = wallet.available_amount || 0;
  const pending   = wallet.pending_amount   || 0;
  const isFrozen  = banking?.withdrawalFrozen;

  return (
    <div className="tw-page">
      <div className="tw-wrapper">

        {/* ── Header ── */}
        <div className="tw-header">
          <button className="tw-back-btn" onClick={() => navigate(-1)} aria-label="Go back">
            <svg xmlns="http://www.w3.org/2000/svg" width="0.5em" height="1em" viewBox="0 0 12 24">
              <path d="M0 0h12v24H0z" fill="none"/>
              <path fill="currentColor" fillRule="evenodd" d="M10 19.438L8.955 20.5l-7.666-7.79a1.02 1.02 0 0 1 0-1.42L8.955 3.5L10 4.563L2.682 12z"/>
            </svg>
          </button>
          <span className="tw-page-title">Wallet</span>
        </div>

        {/* ── Balance ── */}
        <div className="tw-balance-wrap">
          <div className="tw-balance-top">
            <div>
              <div className="tw-balance-label">Available Funds</div>
              <div className="tw-balance-big">
                <span className="tw-currency">R</span>
                <span className="tw-amount">{available.toLocaleString('en-ZA', { minimumFractionDigits:2, maximumFractionDigits:2 })}</span>
              </div>
            </div>
            {pending > 0 && (
              <div className="tw-pending-pill">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/></svg>
                R {pending.toFixed(2)} in escrow
              </div>
            )}
          </div>
        </div>

        {/* ── Main row ── */}
        <div className="tw-row">

          {/* ── LEFT: transactions ── */}
          <div className="tw-col-left">
            <div className="tw-col-header">
              <h2>Transactions</h2>
              <div className="tw-filter-tabs">
                {['all','received','withdrawal'].map(f => (
                  <button key={f} className={`tw-filter-tab ${filter === f ? 'active' : ''}`} onClick={() => setFilter(f)}>
                    {f.charAt(0).toUpperCase() + f.slice(1)}
                  </button>
                ))}
              </div>
            </div>

            <div className="tw-tx-list">
              {loading ? (
                <div className="tw-empty">Loading…</div>
              ) : filtered.length === 0 ? (
                <div className="tw-empty">No transactions yet.</div>
              ) : (
                filtered.map((tx, i) => <TxItem key={i} tx={tx}/>)
              )}
            </div>
          </div>

          {/* ── RIGHT: withdraw + bank ── */}
          <div className="tw-col-right">

            {/* Withdraw card */}
            <div className="tw-card">
              <h3 className="tw-card-title">Withdraw funds</h3>
              <div className="tw-card-balance">
                Available: <strong>R {available.toFixed(2)}</strong>
              </div>

              {isFrozen && (
                <div className="tw-warning-box">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                  <span>Withdrawals frozen until banking details are verified (24h security hold).</span>
                </div>
              )}

              <div className="tw-quick-btns">
                {[500, 1000, 2000].map(v => (
                  <button key={v} className="tw-quick-amt" onClick={() => setQuick(v)} disabled={available < v || isFrozen}>R{v}</button>
                ))}
                <button className="tw-quick-amt" onClick={() => setQuick('full')} disabled={available < MIN_WITHDRAWAL || isFrozen}>Full</button>
              </div>

              <div className="tw-amount-row">
                <span className="tw-amount-prefix">R</span>
                <input
                  ref={amountRef}
                  type="number"
                  className="tw-amount-input"
                  placeholder="0.00"
                  min={MIN_WITHDRAWAL}
                  max={available}
                  value={withdrawAmount}
                  onChange={e => handleAmountChange(e.target.value)}
                  disabled={isFrozen}
                />
              </div>

              {showFeeBox && (
                <div className="tw-fee-box">
                  <div className="tw-fee-row"><span>Requested</span><span>R {parseFloat(withdrawAmount).toFixed(2)}</span></div>
                  <div className="tw-fee-row tw-fee-item"><span>Withdrawal fee</span><span>− R {WITHDRAWAL_FEE.toFixed(2)}</span></div>
                  <div className="tw-divider"/>
                  <div className="tw-fee-row tw-fee-net"><span>You receive</span><span>R {netAmount.toFixed(2)}</span></div>
                </div>
              )}

              <p className="tw-min-note">Minimum withdrawal: R{MIN_WITHDRAWAL} · Fee: R{WITHDRAWAL_FEE}</p>

              <button
                className="tw-withdraw-btn"
                onClick={handleWithdraw}
                disabled={!showFeeBox || withdrawing || isFrozen || !banking?.hasDetails}
              >
                {withdrawing ? <span className="tw-spinner"/> : 'Confirm withdrawal'}
              </button>

              {!banking?.hasDetails && (
                <p className="tw-no-bank-note">Add your banking details below before withdrawing.</p>
              )}
            </div>

            {/* Banking details card */}
            <div className="tw-card">
              <div className="tw-bank-header">
                <h3 className="tw-card-title">Payout account</h3>
                <button className="tw-change-btn" onClick={() => setShowBankModal(true)}>
                  {banking?.hasDetails ? 'Change' : 'Add details'}
                </button>
              </div>

              {banking?.hasDetails ? (
                <div className="tw-bank-info">
                  <div className="tw-bank-icon">
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
                      <path d="M0 0h24v24H0z" fill="none"/>
                      <path fill="currentColor" d="M16.688 0c-.2.008-.393.044-.594.094L2.5 3.406C.892 3.8-.114 5.422.281 7.031l1.906 7.782A2.99 2.99 0 0 0 4 16.875V15c0-2.757 2.243-5 5-5h12.594l-1.875-7.719A3.004 3.004 0 0 0 16.687 0zm1.218 4.313l.813 3.406l-3.375.812l-.844-3.375zM9 12c-1.656 0-3 1.344-3 3v8c0 1.656 1.344 3 3 3h14c1.656 0 3-1.344 3-3v-8c0-1.656-1.344-3-3-3zm0 1.594h14c.771 0 1.406.635 1.406 1.406v1H7.594v-1c0-.771.635-1.406 1.406-1.406M7.594 19h16.812v4c0 .771-.635 1.406-1.406 1.406H9A1.414 1.414 0 0 1 7.594 23z"/>
                    </svg>
                  </div>
                  <div>
                    <div className="tw-bank-name">{banking.bankName} — {banking.accountType}</div>
                    <div className="tw-bank-acct">{banking.accountHolder} · {banking.accountNumberMasked}</div>
                  </div>
                </div>
              ) : (
                <div className="tw-no-bank">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2"/></svg>
                  <p>No payout account added yet</p>
                </div>
              )}
            </div>

          </div>
        </div>
      </div>

      {/* Modals */}
      {showBankModal && (
        <BankModal
          onClose={() => { setShowBankModal(false); fetchAll(); }}
          onSaved={() => {}}
        />
      )}

      {showWithdrawModal && withdrawResult && (
        <WithdrawModal
          amount={withdrawResult.requested}
          netAmount={withdrawResult.netPayout}
          bankName={withdrawResult.bank}
          maskedAcct={withdrawResult.accountMasked}
          onClose={() => { setShowWithdrawModal(false); setWithdrawResult(null); }}
        />
      )}
    </div>
  );
};

export default TutorWallet;